import type { ThemeProperties } from '@unifyapps/defs/theme/types';
import { getFontFileExtension } from './getCustomFontPreloadLinks';

// CSS `@font-face` `format()` keywords keyed by file extension (NOT the stored `font.format` display
// label like 'TTF'/'OTF', which is not a valid format() keyword)
const FORMAT_BY_EXTENSION: Record<string, string> = {
  woff2: 'woff2',
  woff: 'woff',
  ttf: 'truetype',
  otf: 'opentype',
  eot: 'embedded-opentype',
  svg: 'svg',
};

// font.name is interpolated raw into a <style>; strip CSS-structural chars that could break out of the
// rule. Legitimate family names never contain these, so the result stays byte-identical to the value
// the font-family selector writes to the token — which is required for the family to actually match.
const sanitizeFamily = (name: string): string => name.replace(/["{};<>\\]/g, '').trim();

const srcFor = (filePath: string): string => {
  const format = FORMAT_BY_EXTENSION[getFontFileExtension(filePath) ?? ''];
  return format ? `url("${filePath}") format("${format}")` : `url("${filePath}")`;
};

/**
 * Builds the `@font-face` rules that bind a theme's uploaded custom fonts to their family names, so
 * that `font-family: "<name>"` (written to the token by the font-family selector) actually resolves
 * at runtime. Without these the files are only preloaded, never registered, and text falls back to a
 * system font. The family name is the raw `font.name` (matching the token value), one block per file.
 *
 * `@font-face` is an at-rule — the caller MUST place this at the top level of the stylesheet, never
 * inside a scoped selector block.
 */
export function getCustomFontFaceCss(
  customFonts: ThemeProperties['customFonts'] | undefined,
): string {
  if (!customFonts?.length) return '';

  let css = '';
  for (const font of customFonts) {
    const family = font.name ? sanitizeFamily(font.name) : '';
    if (!family || !font.files?.length) continue;

    const isVariable = font.type === 'variable';
    for (const file of font.files) {
      if (!file.filePath) continue;

      const declarations = [
        `font-family:"${family}"`,
        `src:${srcFor(file.filePath)}`,
        'font-display:swap',
      ];
      if (isVariable) {
        // variable fonts store a weight range under the `wght` axis instead of a fixed weight
        const weightAxis = file.axes?.[0]?.wght;
        if (weightAxis) declarations.push(`font-weight:${weightAxis.from} ${weightAxis.to}`);
      } else {
        if (file.weight !== undefined) declarations.push(`font-weight:${file.weight}`);
        declarations.push(`font-style:${file.italic ? 'italic' : 'normal'}`);
      }
      css += `@font-face{${declarations.join(';')};}`;
    }
  }
  return css;
}
